import { useState, useEffect, useCallback, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { cn } from "@/lib/utils";
import "./UniversalSettingsDialog.scss";

export interface SettingsCategory {
    id: string;
    title: string;
    icon: ReactNode;
    description: string;
}

export interface SettingsSection {
    id: string;
    title: string;
    categories: SettingsCategory[];
}

interface UniversalSettingsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    initialCategory?: string;
    sections: SettingsSection[];
    title: string;
    icon?: ReactNode;
    renderContent: (categoryId: string) => ReactNode;
    defaultCategory?: string;
}

const UniversalSettingsDialog = ({ 
    isOpen, 
    onClose, 
    initialCategory,
    sections,
    title,
    icon,
    renderContent,
    defaultCategory
}: UniversalSettingsDialogProps) => {
    const isMobile = useIsMobile();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Get all categories for easy lookup
    const allCategories = sections.flatMap(section => section.categories);
    const firstCategory = allCategories[0]?.id || defaultCategory || '';
    
    const [activeCategory, setActiveCategory] = useState<string>(
        initialCategory || firstCategory
    );

    // Callbacks
    const handleCategoryChange = useCallback((categoryId: string) => {
        setActiveCategory(categoryId);
        if (isOpen) {
            setSearchParams({ settings: categoryId });
        }
    }, [isOpen, setSearchParams]);

    const handleClose = useCallback(() => {
        // Remove settings parameter from URL when dialog closes
        const url = new URL(window.location.href);
        url.searchParams.delete('settings');
        window.history.pushState({}, '', url.toString());
        onClose();
    }, [onClose]);

    // Initialize category from URL or props when dialog opens
    useEffect(() => {
        if (isOpen) {
            const categoryFromUrl = searchParams.get('settings');
            const validCategory = categoryFromUrl && allCategories.find(cat => cat.id === categoryFromUrl);
            const categoryToUse = validCategory ? validCategory.id : (initialCategory || firstCategory);
            
            if (categoryToUse !== activeCategory) {
                setActiveCategory(categoryToUse);
            }
            
            // Update URL if it doesn't match the current category
            if (!categoryFromUrl || categoryFromUrl !== categoryToUse) {
                setSearchParams({ settings: categoryToUse });
            }
        }
    }, [isOpen, searchParams, allCategories, initialCategory, firstCategory, activeCategory, setSearchParams]);

    if (isMobile) {
        return (
            <Sheet open={isOpen} onOpenChange={handleClose}>
                <SheetContent side="bottom" className="h-[90vh] p-0">
                    <SheetHeader className="px-6 py-4 border-b">
                        <SheetTitle className="flex items-center gap-2">
                            {icon}
                            {title}
                        </SheetTitle>
                    </SheetHeader>
                    
                    <div className="flex flex-col h-full">
                        {/* Mobile Category Selector */}
                        <div className="px-6 py-4 border-b">
                            <Select value={activeCategory} onValueChange={handleCategoryChange}>
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {allCategories.map((category) => (
                                        <SelectItem key={category.id} value={category.id}>
                                            {category.title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Mobile Content */}
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                            {renderContent(activeCategory)}
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="universal-settings-dialog">
                <div className="settings-container">
                    {/* Desktop Side Menu */}
                    <div className="settings-sidebar">
                        {sections.map((section, sectionIndex) => (
                            <div key={section.id} className="sidebar-section">
                                <div className="section-title text-sm px-3 py-2">
                                    {section.title}
                                </div>
                                <nav className="space-y-1">
                                    {section.categories.map((category) => (
                                        <button
                                            key={category.id}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleCategoryChange(category.id);
                                            }}
                                            className={cn(
                                                "w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors text-sm font-normal",
                                                activeCategory === category.id
                                                    ? "bg-[var(--button-background-highlight)] text-white"
                                                    : "text-[var(--foreground)] hover:bg-[var(--button-background-highlight)] hover:text-white"
                                            )}
                                        >
                                            {category.icon}
                                            <div>{category.title}</div>
                                        </button>
                                    ))}
                                </nav>
                                {/* Add divider between sections except for the last one */}
                                {sectionIndex < sections.length - 1 && (
                                    <div className="section-divider" />
                                )}
                            </div>
                        ))}
                    </div>
                    
                    {/* Desktop Content */}
                    <div className="settings-main">
                        {renderContent(activeCategory)}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default UniversalSettingsDialog;

